/**
 * Login page — single sign-in form.
 *
 * On success the auth slice fills user + accessToken + refreshToken +
 * secretKey, the PublicRoute guard sees `isAuthed=true` and redirects to
 * `/` (Overview), or back to the page the user originally tried to visit
 * if there's one in router state.
 */

import React, { useState, type FormEvent } from 'react';

import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
	clearLoginError,
	loginThunk,
	selectAuthError,
	selectAuthStatus,
} from '../../redux/slices/auth';

export default function LoginPage() {
	const dispatch = useAppDispatch();
	const status = useAppSelector(selectAuthStatus);
	const error = useAppSelector(selectAuthError);

	const [username, setUsername] = useState('');
	const [password, setPassword] = useState('');
	const [touched, setTouched] = useState<{ username: boolean; password: boolean }>({
		username: false,
		password: false,
	});

	const loading = status === 'loading';
	const usernameError = touched.username && !username.trim() ? 'Username is required' : null;
	const passwordError = touched.password && !password ? 'Password is required' : null;

	const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setTouched({ username: true, password: true });
		if (!username.trim() || !password) return;
		dispatch(clearLoginError());
		dispatch(loginThunk({ username: username.trim(), password }));
	};

	return (
		<div className="login-shell">
			<div className="login-card">
				<div className="login-brand">
					<div className="brand-mark">U</div>
					<div>
						<div style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy-900)' }}>
							UCB Dashboard
						</div>
						<div style={{ fontSize: 12, color: 'var(--text-faint)' }}>
							Submission Monitor
						</div>
					</div>
				</div>

				<h1 className="login-title">Sign in</h1>
				<p className="login-sub">
					Use your UCB Dashboard credentials. Admin-provisioned accounts only.
				</p>

				{error && <div className="login-banner" role="alert">{error}</div>}

				<form onSubmit={handleSubmit} autoComplete="on" noValidate>
					<label className={'login-field' + (usernameError ? ' error' : '')}>
						<span>Username</span>
						<input
							type="text"
							name="username"
							value={username}
							autoComplete="username"
							disabled={loading}
							placeholder="admin"
							onChange={(e) => setUsername(e.target.value)}
							onBlur={() => setTouched((t) => ({ ...t, username: true }))}
							aria-invalid={usernameError ? 'true' : 'false'}
						/>
						{usernameError && <p className="login-err">{usernameError}</p>}
					</label>

					<label className={'login-field' + (passwordError ? ' error' : '')}>
						<span>Password</span>
						<input
							type="password"
							name="password"
							value={password}
							autoComplete="current-password"
							disabled={loading}
							placeholder="••••••••"
							onChange={(e) => setPassword(e.target.value)}
							onBlur={() => setTouched((t) => ({ ...t, password: true }))}
							aria-invalid={passwordError ? 'true' : 'false'}
						/>
						{passwordError && <p className="login-err">{passwordError}</p>}
					</label>

					<button type="submit" className="login-submit" disabled={loading}>
						{loading ? 'Signing in…' : 'Sign in'}
					</button>
				</form>

				<div
					style={{
						fontSize: 11,
						color: 'var(--text-faint)',
						marginTop: 18,
						textAlign: 'center',
					}}
				>
					Requests encrypted end-to-end · JWT session · bcrypt password hashing
				</div>
			</div>
		</div>
	);
}
